import { useRef, useEffect, useState } from 'react';
import useAdvancedEditorStore from '../../store/useAdvancedEditorStore';
import { cloneImageData } from '../../utils/imageProcessing';
import { applyMagicWand } from '../../utils/magicWand';

/**
 * Editor de Canvas com suporte a múltiplas camadas
 *
 * Arquitetura:
 * - Canvas principal: exibe a imagem original
 * - Canvas de máscara: exibe a máscara editável
 * - Canvas de composição: combina imagem + máscara para preview
 * - Canvas de overlay: mostra o cursor do brush
 *
 * Ferramentas:
 * - Brush: pinta na máscara (adiciona áreas)
 * - Eraser: apaga na máscara (remove áreas)
 * - Magic Wand: seleção automática por cor
 */
const AdvancedCanvas = () => {
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const renderRequestRef = useRef(null);
  const containerRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });

  const {
    originalImage,
    mask,
    currentTool,
    brushSize,
    magicWandTolerance,
    viewMode,
    zoom,
    setZoom,
    setMask,
  } = useAdvancedEditorStore();

  /**
   * Inicializa o canvas quando a imagem é carregada
   */
  useEffect(() => {
    if (!originalImage) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(originalImage, 0, 0);

    console.log('✅ Canvas inicializado:', originalImage.width, 'x', originalImage.height);
  }, [originalImage]);

  /**
   * Atualiza o canvas de máscara quando a máscara muda
   */
  useEffect(() => {
    if (!mask) return;

    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) return;

    const ctx = maskCanvas.getContext('2d');
    ctx.putImageData(mask, 0, 0);

    console.log('✅ Máscara atualizada:', mask.width, 'x', mask.height);
  }, [mask]);

  /**
   * Atualiza a visualização baseada no modo atual
   */
  useEffect(() => {
    renderComposite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mask, viewMode, originalImage]);

  /**
   * Renderiza a composição final (imagem + máscara)
   */
  const renderComposite = () => {
    if (!originalImage || !mask) return;

    const canvas = canvasRef.current;
    const maskCanvas = maskCanvasRef.current;
    const ctx = canvas.getContext('2d');

    // Limpa canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (viewMode === 'original') {
      // Mostra apenas a imagem original
      ctx.drawImage(originalImage, 0, 0);
    } else if (viewMode === 'mask') {
      // Mostra apenas a máscara
      ctx.putImageData(mask, 0, 0);
    } else {
      // Desenha imagem original
      ctx.drawImage(originalImage, 0, 0);

      // Pega dados da máscara atual do maskCanvas (mais rápido)
      const maskCtx = maskCanvas.getContext('2d');
      const currentMask = maskCtx.getImageData(0, 0, canvas.width, canvas.height);

      // Pega dados da imagem para aplicar overlay
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const maskData = currentMask.data;

      // Aplica overlay rosa APENAS nas áreas removidas (maskValue === 0)
      // Imagem original sempre visível com opacidade 100%
      for (let i = 0; i < data.length; i += 4) {
        const maskValue = maskData[i]; // Valor da máscara (0-255)

        if (maskValue === 0) {
          // Área removida - aplica overlay rosa sobre a imagem original
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Mistura a cor original com rosa (255, 105, 180) - 60% rosa, 40% original
          data[i] = (r * 0.4 + 255 * 0.6) | 0;     // R
          data[i + 1] = (g * 0.4 + 105 * 0.6) | 0; // G
          data[i + 2] = (b * 0.4 + 180 * 0.6) | 0; // B
          data[i + 3] = 200; // Alpha semi-transparente para mostrar que será removido
        }
        // Áreas mantidas (maskValue > 0): não altera nada, mantém imagem original com alpha 255
      }

      ctx.putImageData(imageData, 0, 0);
    }
  };

  /**
   * Handler para zoom com scroll
   */
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setZoom(Math.max(0.1, Math.min(5, zoom + delta)));
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [zoom, setZoom]);

  /**
   * Handler para tecla Espaço (pan mode)
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !isPanning) {
        e.preventDefault();
        setIsPanning(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPanning(false);
        setPanStart(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isPanning]);

  /**
   * Handler para início do desenho
   */
  const handleMouseDown = (e) => {
    console.log('🖱️ Mouse down - Tool:', currentTool);

    if (isPanning) {
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      return;
    }

    const pos = getMousePos(e);
    console.log('📍 Position:', pos);

    // Se for varinha mágica, aplica seleção imediatamente
    if (currentTool === 'magic-wand-add' || currentTool === 'magic-wand-remove') {
      console.log('✨ Aplicando magic wand');
      applyMagicWandSelection(pos);
      return;
    }

    console.log('✏️ Iniciando desenho com', currentTool);
    setIsDrawing(true);
    setLastPos(pos);
    drawAtPosition(pos);
  };

  /**
   * Handler para movimento do mouse
   */
  const handleMouseMove = (e) => {
    if (isPanning && panStart) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    const pos = getMousePos(e);

    // Atualiza cursor
    updateCursor(pos);

    if (!isDrawing) return;

    // Desenha linha do último ponto até o ponto atual
    if (lastPos) {
      drawLine(lastPos, pos);
    }
    setLastPos(pos);
  };

  /**
   * Handler para fim do desenho
   */
  const handleMouseUp = () => {
    if (isPanning) {
      setPanStart(null);
      return;
    }

    if (isDrawing) {
      // Atualiza máscara no store (adiciona ao histórico)
      const maskCanvas = maskCanvasRef.current;
      const ctx = maskCanvas.getContext('2d');
      const newMask = ctx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
      setMask(newMask, true);
    }

    setIsDrawing(false);
    setLastPos(null);
  };

  /**
   * Handler para saída do mouse
   */
  const handleMouseLeave = () => {
    setIsDrawing(false);
    setLastPos(null);
    setPanStart(null);
    clearCursor();
  };

  /**
   * Obtém posição do mouse relativa ao canvas
   */
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  /**
   * Agenda renderização otimizada
   */
  const scheduleRender = () => {
    if (renderRequestRef.current) {
      cancelAnimationFrame(renderRequestRef.current);
    }
    renderRequestRef.current = requestAnimationFrame(() => {
      renderComposite();
      renderRequestRef.current = null;
    });
  };

  /**
   * Aplica seleção da varinha mágica
   */
  const applyMagicWandSelection = (pos) => {
    if (!mask) return;

    const canvas = canvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;

    // Determina o modo baseado na ferramenta atual
    const mode = currentTool === 'magic-wand-add' ? 'add' : 'remove';

    // Aplica varinha mágica
    const result = applyMagicWand(
      canvas,
      mask,
      Math.floor(pos.x),
      Math.floor(pos.y),
      magicWandTolerance,
      mode
    );

    // Se retornou um objeto com máscara e seleção, desenha o pontilhado
    if (result.mask && result.selection) {
      // Atualiza máscara
      setMask(result.mask, true);

      // Desenha borda pontilhada na área selecionada
      drawDottedSelection(result.selection);

      // Renderiza imediatamente
      renderComposite();

      // Remove o pontilhado após 1 segundo
      setTimeout(() => {
        const ctx = overlayCanvas.getContext('2d');
        ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
      }, 1000);
    } else {
      // Fallback: se retornou apenas a máscara
      setMask(result, true);
      renderComposite();
    }
  };

  /**
   * Desenha borda pontilhada na área selecionada
   */
  const drawDottedSelection = (selection) => {
    const overlayCanvas = overlayCanvasRef.current;
    const ctx = overlayCanvas.getContext('2d');

    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    // Configura estilo pontilhado
    ctx.strokeStyle = currentTool === 'magic-wand-add' ? '#7eb715' : '#ff69b4';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);

    // Desenha contorno da seleção
    ctx.beginPath();

    for (let y = 0; y < selection.height; y++) {
      for (let x = 0; x < selection.width; x++) {
        const idx = y * selection.width + x;
        if (selection.data[idx] === 1) {
          // Verifica se é borda (tem vizinho não selecionado)
          const isBorder =
            x === 0 || x === selection.width - 1 ||
            y === 0 || y === selection.height - 1 ||
            selection.data[idx - 1] === 0 ||
            selection.data[idx + 1] === 0 ||
            selection.data[idx - selection.width] === 0 ||
            selection.data[idx + selection.width] === 0;

          if (isBorder) {
            ctx.fillRect(x, y, 1, 1);
          }
        }
      }
    }

    ctx.stroke();
    ctx.setLineDash([]);
  };

  /**
   * Desenha em uma posição específica
   */
  const drawAtPosition = (pos) => {
    console.log('🎨 Drawing at:', pos, 'tool:', currentTool, 'brushSize:', brushSize);

    const maskCanvas = maskCanvasRef.current;
    if (!maskCanvas) {
      console.error('❌ maskCanvas não encontrado!');
      return;
    }

    const ctx = maskCanvas.getContext('2d');

    // eraser-add restaura (adiciona branco), eraser-remove apaga (remove)
    ctx.globalCompositeOperation =
      currentTool === 'eraser-add' ? 'source-over' : 'destination-out';

    console.log('🖌️ Composite operation:', ctx.globalCompositeOperation);

    ctx.fillStyle = 'white';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
    ctx.fill();

    console.log('✅ Desenho realizado, chamando renderComposite');
    // Atualiza visualização imediatamente
    renderComposite();
  };

  /**
   * Desenha linha entre dois pontos
   */
  const drawLine = (from, to) => {
    const maskCanvas = maskCanvasRef.current;
    const ctx = maskCanvas.getContext('2d');

    // eraser-add restaura (adiciona branco), eraser-remove apaga (remove)
    ctx.globalCompositeOperation =
      currentTool === 'eraser-add' ? 'source-over' : 'destination-out';

    ctx.strokeStyle = 'white';
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();

    // Atualiza visualização imediatamente sem throttle
    renderComposite();
  };

  /**
   * Atualiza cursor de preview
   */
  const updateCursor = (pos) => {
    const overlayCanvas = overlayCanvasRef.current;
    const ctx = overlayCanvas.getContext('2d');

    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);

    // Varinha mágica - apenas cursor do sistema, sem preview
    if (currentTool === 'magic-wand-add' || currentTool === 'magic-wand-remove') {
      return;
    }

    // Cor do cursor baseada na ferramenta
    if (currentTool === 'eraser-add') {
      // Borracha Restaurar - verde (adiciona áreas de volta)
      ctx.strokeStyle = '#7eb715';
      ctx.fillStyle = 'rgba(126, 183, 21, 0.2)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else if (currentTool === 'eraser-remove') {
      // Borracha Remover - rosa (remove áreas)
      ctx.strokeStyle = '#ff69b4';
      ctx.fillStyle = 'rgba(255, 105, 180, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  };

  /**
   * Limpa cursor
   */
  const clearCursor = () => {
    const overlayCanvas = overlayCanvasRef.current;
    const ctx = overlayCanvas.getContext('2d');
    ctx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
  };

  if (!originalImage) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative flex items-center justify-center w-full h-full bg-gray-100 overflow-hidden"
      style={{ cursor: isPanning ? 'grab' : (currentTool === 'magic-wand-add' || currentTool === 'magic-wand-remove') ? 'crosshair' : 'default' }}
    >
      {/* Checkerboard pattern para mostrar transparência */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        }}
      />

      {/* Container dos canvas com zoom e pan */}
      <div
        className="relative"
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
          transformOrigin: 'center',
          transition: isPanning ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        {/* Canvas principal (imagem + composição) */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0"
          width={originalImage?.width || 0}
          height={originalImage?.height || 0}
          style={{ imageRendering: 'crisp-edges' }}
        />

        {/* Canvas de máscara (invisível, usado para edição) */}
        <canvas
          ref={maskCanvasRef}
          className="absolute top-0 left-0 opacity-0 pointer-events-none"
          width={originalImage?.width || 0}
          height={originalImage?.height || 0}
        />

        {/* Canvas de overlay (cursor) */}
        <canvas
          ref={overlayCanvasRef}
          className="absolute top-0 left-0 pointer-events-none"
          width={originalImage?.width || 0}
          height={originalImage?.height || 0}
        />

        {/* Canvas interativo (captura eventos) */}
        <canvas
          className="relative"
          style={{ cursor: isPanning ? 'grab' : 'crosshair' }}
          width={originalImage?.width || 0}
          height={originalImage?.height || 0}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        />
      </div>

      {/* Controles de Zoom */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 bg-white rounded-lg shadow-lg p-2">
        {/* Zoom In */}
        <button
          onClick={() => setZoom(Math.min(5, zoom + 0.2))}
          disabled={zoom >= 5}
          className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Aumentar zoom (Ctrl + Scroll)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>

        {/* Indicador de Zoom */}
        <div className="w-10 h-10 flex items-center justify-center text-xs font-semibold text-gray-700">
          {Math.round(zoom * 100)}%
        </div>

        {/* Zoom Out */}
        <button
          onClick={() => setZoom(Math.max(0.1, zoom - 0.2))}
          disabled={zoom <= 0.1}
          className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Diminuir zoom (Ctrl + Scroll)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>

        {/* Reset Zoom */}
        <button
          onClick={() => setZoom(1)}
          disabled={zoom === 1}
          className="w-10 h-10 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Resetar zoom (100%)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Dica de Zoom */}
      <div className="absolute bottom-4 left-4 bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded">
        <kbd className="px-1.5 py-0.5 bg-white bg-opacity-20 rounded">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-white bg-opacity-20 rounded">Scroll</kbd> para zoom
      </div>
    </div>
  );
};

export default AdvancedCanvas;
